/** @prettier */

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
//interface import
import * as apiIF from '../../Type/api';

/**
 * グループを選択するセレクトボックス
 * @param props props
 * - groupID 選択しているグループのID
 * - setGroupID グループIDのセット関数
 * - groupInfo グループ情報
 * @returns JSX
 */
const SingleGroupSelectBox = (props: {
  groupID: string;
  setGroupID: React.Dispatch<React.SetStateAction<string>>;
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
}) => {
  /** グループのセレクトボックスの選択肢 */
  const groupSelectionItem: {value: string; label: string}[] = [];

  //APIで取得したグループをセレクトボックスの選択肢に追加
  for (const info of props.groupInfo) {
    //ユニットタイプがSL以外は除外
    if (info['unit-system'] !== 'SL') continue;
    //グループ名がない場合は除外
    if (info['group-name'] === undefined) continue;

    //グループのセレクトボックスの選択肢に追加
    groupSelectionItem.push({
      value: info['group-id'],
      label: info['group-name'],
    });
  }

  return (
    <TextFieldSelectBox
      disabled={groupSelectionItem.length === 0}
      choices={groupSelectionItem}
      value={props.groupID}
      setValue={props.setGroupID}
      id="mv-todayScheduleSelect"
      class="schedule__contents__groupSelectArea__selectBox"
      menuClass="schedule__contents__groupSelectArea__selectBox__menu"
    />
  );
};

/**
 * 複数のグループを選択するセレクトボックス
 * @param props props
 * @returns JSX.Element
 */
const MultiGroupSelectBox = (props: {
  /** 選択状態のグループID */
  selectedGroupID: string[];
  /** グループIDのセット関数 */
  setSelectedGroupID: React.Dispatch<React.SetStateAction<string[]>>;
  /** グループ情報 */
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
  /** 選択肢から除外するグループID */
  excludeGroupID?: string;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /** グループのセレクトボックスの選択肢 */
  const groupSelectionItem: {value: string; label: string}[] = [];
  /** グループID一覧の初期値 */
  const groupSelectionID: string[] = [];

  //APIで取得したグループをセレクトボックスの選択肢に追加
  for (let info of props.groupInfo) {
    //ユニットタイプがSL以外は除外
    if (info['unit-system'] !== 'SL') continue;
    //除外対象のグループIDの場合は除外
    if (info['group-id'] === props.excludeGroupID) continue;
    //グループ名がない場合は除外
    if (info['group-name'] === undefined) continue;

    //グループのセレクトボックスの選択肢に追加
    groupSelectionItem.push({
      value: info['group-id'],
      label: info['group-name'],
    });
    groupSelectionID.push(info['group-id']);
  }

  /**
   * 選択したグループのグループ名を検索
   * @param selected 選択したグループID一覧
   */
  const searchGroupName = (selected: string[]) => {
    const selectGroupNames = groupSelectionItem
      .filter(item => {
        return selected.includes(item.value);
      })
      .map(info => info.label);
    return selectGroupNames.join(', ');
  };

  /**
   * セレクトボックス内のグループ選択時処理
   * @param event
   */
  const handleChange = (
    event: SelectChangeEvent<typeof props.selectedGroupID>
  ) => {
    const {
      target: {value},
    } = event;

    //All Groupsが選択されている場合
    if (value[value.length - 1] === 'all') {
      props.setSelectedGroupID(
        props.selectedGroupID.length === groupSelectionID.length
          ? []
          : groupSelectionID
      );
      return;
    }
    //All Groups以外が選択された場合
    if (typeof value !== 'string') {
      return props.setSelectedGroupID(value);
    }
  };

  //全てのグループを選択
  const isAllSelected =
    groupSelectionID.length > 0 &&
    props.selectedGroupID.length === groupSelectionID.length;

  return (
    <FormControl className="schedule__copyDialog__content__groupSelectArea__selectBox">
      <InputLabel>{trans('GroupSelectBox.label')}</InputLabel>
      <Select
        multiple
        value={props.selectedGroupID}
        onChange={handleChange}
        input={<OutlinedInput label={trans('GroupSelectBox.label')} />}
        renderValue={searchGroupName}
        MenuProps={{PaperProps: {sx: {maxHeight: 350}}}}>
        <MenuItem value="all">
          <ListItemIcon>
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                props.selectedGroupID.length > 0 &&
                props.selectedGroupID.length < groupSelectionID.length
              }
            />
          </ListItemIcon>
          <ListItemText primary={trans('GroupSelectBox.allGroups')} />
        </MenuItem>
        {groupSelectionItem.map(name => (
          <MenuItem
            key={name.value}
            value={name.value}
            className="schedule__copyDialog__content__groupSelectArea__selectBox__menu">
            <Checkbox
              checked={props.selectedGroupID.indexOf(name.value) > -1}
            />
            <Box
              component="span"
              className="schedule__copyDialog__content__groupSelectArea__selectBox__menu__list">
              {name.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export {MultiGroupSelectBox};
export default SingleGroupSelectBox;
